import React, { useMemo } from 'react';
import { Download, Pencil, Eye, Shield, Trash2, FileSpreadsheet, FileText, FileType, FolderOpen } from 'lucide-react';
import { DataTable, Avatar, TableActionButton } from '@/components/ui';
import { TABLE_BACKGROUNDS } from '@/constants/tableBackgrounds';
import { formatDocDate, getFileIconType } from '../utils/documentHelpers';

const FILE_ICONS = {
  excel: FileSpreadsheet,
  word: FileText,
  pdf: FileType,
  other: FolderOpen,
};

export const DocumentsTableView = ({
  docs,
  onOpen,
  onPermissions,
  onDelete,
  onDownload,
  canEdit,
  isOwner,
  apiBaseUrl,
  sectionId,
}) => {
  const columns = useMemo(
    () => [
      {
        header: 'Document Name',
        minWidth: '120px',
        className: 'w-[300px] max-w-[300px]',
        headerClassName: 'w-[300px] max-w-[300px]',
        render: (doc) => {
          const type = getFileIconType(doc);
          const Icon = FILE_ICONS[type] || FolderOpen;
          return (
            <button
              type="button"
              onClick={() => onOpen(doc)}
              className="flex items-center gap-3 text-left w-full group"
            >
              <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 text-[12px] truncate group-hover:text-brand-blue transition-colors">
                  {doc.title}
                </div>
                <div className="text-[10px] text-slate-400 truncate max-w-[200px]">{doc.fileName}</div>
              </div>
            </button>
          );
        },
      },
      {
        header: 'Category',
        minWidth: '120px',
        render: (doc) => (
          <div className="text-xs text-slate-600 font-medium">
            {doc.category || '—'}
          </div>
        ),
      },
      {
        header: 'Notes',
        minWidth: '200px',
        render: (doc) => (
          <div className="text-xs text-slate-500 truncate max-w-[200px]" title={doc.notes}>
            {doc.notes || '—'}
          </div>
        ),
      },
      /* {
        header: 'File Locations',
        minWidth: '220px',
        render: (doc) => (
          <div className="flex flex-col gap-1 w-full">
            <div className="text-[10px] break-all" title={doc.driveFileId ? `https://drive.google.com/file/d/${doc.driveFileId}/view` : 'Not synced'}>
              <span className="font-semibold text-slate-700">Drive:</span>{' '}
              <span className="text-slate-500">
                {doc.driveFileId ? (
                  `https://drive.google.com/file/d/${doc.driveFileId}/view`
                ) : (
                  'Not synced'
                )}
              </span>
            </div>
          </div>
        ),
      }, */
      {
        header: 'Uploaded By',
        minWidth: '160px',
        render: (doc) => (
          <div className="flex flex-col justify-center text-left w-full">
            <div className="text-xs font-semibold text-slate-700">{doc.uploadedBy?.name || 'System'}</div>
            <div className="text-[10px] text-slate-400">{formatDocDate(doc.createdAt)}</div>
          </div>
        ),
      },
      {
        header: 'Last Modified',
        minWidth: '160px',
        render: (doc) => {
          const lastEdit =
            doc.editHistory?.length > 0 ? doc.editHistory[doc.editHistory.length - 1] : null;
          const lastModifier = lastEdit?.user?.name || doc.uploadedBy?.name || 'System';
          const lastModifiedDate = lastEdit?.timestamp || doc.updatedAt || doc.createdAt;
          return (
            <div className="flex flex-col justify-center text-left w-full">
              <div className="text-xs font-semibold text-slate-700">{lastModifier}</div>
              <div className="text-[10px] text-slate-400">{formatDocDate(lastModifiedDate)}</div>
            </div>
          );
        },
      },
      {
        header: 'Actions',
        minWidth: '150px',
        headerClassName: '!text-center !justify-center',
        render: (doc) => (
          <div className="flex items-center justify-center gap-1">
            <TableActionButton
              icon={Download}
              title="Download"
              onClick={() => onDownload ? onDownload(doc) : window.open(`${apiBaseUrl}${doc.fileUrl}`, '_blank')}
            />
            {canEdit(doc) && (
              <>
                {sectionId !== 'drawings' && (
                  <TableActionButton 
                    icon={(doc.fileType?.includes('pdf') || doc.fileName?.toLowerCase().endsWith('.pdf')) ? Eye : Pencil} 
                    title={(doc.fileType?.includes('pdf') || doc.fileName?.toLowerCase().endsWith('.pdf')) ? "View" : "Edit"} 
                    onClick={() => onOpen(doc)} 
                  />
                )}
                <TableActionButton icon={Shield} title="Permissions" onClick={() => onPermissions(doc)} />
                {isOwner && (
                  <TableActionButton
                    icon={Trash2}
                    variant="danger"
                    title="Delete"
                    onClick={() => onDelete(doc._id)}
                  />
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    [onOpen, onPermissions, onDelete, canEdit, isOwner, apiBaseUrl, sectionId]
  );

  return (
    <DataTable
      columns={columns}
      data={docs}
      backgroundImage={TABLE_BACKGROUNDS.documents}
      maxHeight="max-h-[420px]"
      hideBorder={true}
    />
  );
};
